import { Box, Button, CardMedia, Tooltip, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { useTranslation } from "react-i18next";
import AppCard from "@plannel/components/ui/AppCard";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";

// AI 시나리오 플래닝 화면의 메뉴 key (TabMenuList 의 AI_SCENARIO_PLANNING lv3).
const SCENARIO_PLANNING_MENU_KEYS = { BF: 6021, DP: 6022, IP: 6023, RP: 6024, MP: 6025 };

/**
 * 확정을 거치지 않은 시나리오 플래닝 임시 버전인가.
 *
 * delFlg 단독으로는 판정할 수 없다. 카드의 자물쇠 토글이 사용자 조작으로 그 값을 바꾼다.
 * 백엔드도 같은 두 조건으로 릴리즈를 막는다(PlanVersionService.isUnconfirmedScenarioVersion).
 */
export const isUnconfirmedScenarioVersion = (version) =>
  version?.verType === "SCENARIO_PLANNING" && !!version?.delFlg;

/**
 * 릴리즈 버튼 자리에 대신 놓는 안내 카드.
 *
 * 시나리오 플래닝 버전이 정규 버전이 되는 통로는 확정 하나다.
 * 릴리즈로 우회하면 어느 시나리오를 골랐는지 기록이 남지 않고, 남은 비교용 버전도 정리되지 않는다.
 */
export default function UnconfirmedScenarioNotice({ moduleCd, height }) {
  const { t } = useTranslation();
  const { isPermitted } = useMenuAccess();
  const menuKey = SCENARIO_PLANNING_MENU_KEYS[moduleCd];
  // 시나리오 플래닝 화면이 이 테넌트/사용자 권한 밖이면 이동 버튼을 내보내지 않는다(안내 문구는 남긴다).
  const canGo = menuKey != null && isPermitted(menuKey);

  const goScenarioPlanning = () => {
    if (menuKey == null) return;
    window.dispatchEvent(new CustomEvent("plannel:openMenu", { detail: { key: menuKey } }));
  };

  // 툴팁 기본 maxWidth 는 300px 라 이 문장이 접힌다.
  // 제한을 풀고 줄바꿈을 막아 한 줄로 보여준다.
  // 전역 테마 대신 이 인스턴스에만 준다. 다른 화면의 툴팁 폭까지 풀 이유가 없다.
  return (
    <Tooltip title={t("MSG_ScenarioPlanningNotConfirmed")} placement="top"
      slotProps={{ tooltip: { sx: { maxWidth: "none", whiteSpace: "nowrap" } } }}>
      <Box sx={{ width: "100%" }}>
        {/* 릴리즈 버튼 자리를 대신하므로 그 버튼과 높이가 같아야 한다.
            size="large" 버튼은 상하 패딩 8px + lineHeight 1.75 라 폰트 스케일을 따라 높이가 움직인다.
            그래서 높이를 고정하지 않고 같은 박스 모델을 쓴다.
            height 를 받은 화면은 컨테이너가 높이를 고정한 경우이므로 그 값을 그대로 쓴다.
            Big 폰트에서도 한 줄을 유지해야 하므로 줄바꿈을 막는다.
            폭이 모자라면 상태 라벨이 말줄임되고 행동 버튼이 살아남는다. */}
        <AppCard sx={{ display: "flex", alignItems: "center", width: "100%", p: 0, mt: 1,
          overflow: "hidden", ...(height ? { height } : { py: "8px" }) }}>
          <CardMedia sx={{ display: "flex", alignItems: "center", alignSelf: "stretch",
            px: "10px", bgcolor: grey[600], minWidth: 0 }}>
            <Typography variant="subtitle1" color="#fff" noWrap>
              {t("unconfirmedScenarioVersion")}
            </Typography>
          </CardMedia>
          <Box sx={{ display: "flex", alignItems: "center", ml: "auto", pr: "4px",
            flexShrink: 0 }}>
            {canGo && (
              <Button size="small" variant="text" onClick={goScenarioPlanning}
                sx={{ textTransform: "none", whiteSpace: "nowrap", minWidth: 0,
                  px: "8px" }}>
                {t("goConfirmScenario")}
              </Button>
            )}
          </Box>
        </AppCard>
      </Box>
    </Tooltip>
  );
}
